import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PreSignDialog from '../PreSign/PreSignDialog';
import PdfLightbox from './PdfLightbox';
import ReactGA from 'react-ga4';

export default function ExploreProducts() {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [showPreSign, setShowPreSign] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfSrc, setPdfSrc] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');

  // Reveal on scroll using IntersectionObserver
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll('[data-reveal]'));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('reveal-in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });
    nodes.forEach(n => io.observe(n));
    return () => io.disconnect();
  }, []);

  // Pointer parallax for background
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    let raf = 0;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const { innerWidth: w, innerHeight: h } = window;
        const x = (e.clientX / w) * 2 - 1; // -1..1
        const y = (e.clientY / h) * 2 - 1; // -1..1
        el.style.setProperty('--mx', String(x));
        el.style.setProperty('--my', String(y));
        raf = 0;
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const cards = [
    {
      key: 'assessment',
      eyebrow: 'For Skill Assessments',
      title: 'SmartAssessments',
      text: 'Upload skills, drills, or match clips and receive structured assessments powered by our AI + expert rubrics.',
      img: '/assets/assessment.jpg',
      fallback: '/assets/1st.jpeg.jpg',
      primary: {
        label: 'Click Here!',
        onClick: () => {
          try { ReactGA.event({ category: 'ExploreProducts', action: 'card_primary_click', label: 'SmartAssessments' }); } catch { }
          try { ReactGA.event({ category: 'ExploreProducts', action: 'pdf_open', label: 'SmartAssessments' }); } catch { }
          setPdfSrc('/Explore Product/2.pdf');
          setPdfTitle('SmartAssessments');
          setPdfOpen(true);
        }
      },
      secondary: {
        label: 'Learn More',
        onClick: () => {
          try { ReactGA.event({ category: 'ExploreProducts', action: 'secondary_click', label: 'SmartAssessments_learn_more' }); } catch { }
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    },
    {
      key: 'match',
      eyebrow: 'Tactical Insights',
      title: 'SmartInsights',
      text: 'Break down full matches with rally stats, momentum charts, and automated highlights. Turn data into winning plans.',
      img: '/assets/match-analysis.jpg',
      fallback: '/assets/2nd.jpeg',
      primary: {
        label: 'Click Here!',
        onClick: () => {
          try { ReactGA.event({ category: 'ExploreProducts', action: 'card_primary_click', label: 'Match Insights' }); } catch { }
          try { ReactGA.event({ category: 'ExploreProducts', action: 'presign_open', label: 'Match Insights' }); } catch { }
          try {
            sessionStorage.setItem('openPreSignShowExploreInfo', '1');
            sessionStorage.setItem('openPreSignStep', '3');
          } catch { }
          setShowPreSign(true);
          window.scrollTo(0, 0);
        }
      },
      secondary: {
        label: 'Sign Up',
        onClick: () => { try { ReactGA.event({ category: 'ExploreProducts', action: 'secondary_click', label: 'MatchInsights_sign_up' }); } catch { } navigate('/signup'); }
      }
    },
    {
      key: 'journal',
      eyebrow: 'Daily Performance OS',
      title: 'SmartJournal',
      text: 'Your training HQ. Plan sessions, capture reflections, track goals, and sync insights to accelerate progress.',
      img: '/assets/journal.jpg',
      fallback: '/assets/3rd.jpeg',
      primary: {
        label: 'Click Here!',
        onClick: () => {
          try { ReactGA.event({ category: 'ExploreProducts', action: 'card_primary_click', label: 'SmartInsights' }); } catch { }
          try { ReactGA.event({ category: 'ExploreProducts', action: 'pdf_open', label: 'SmartInsights' }); } catch { }
          setPdfSrc('/Explore Product/1.pdf');
          setPdfTitle('SmartInsights');
          setPdfOpen(true);
        }
      },
      secondary: {
        label: 'Sign Up',
        onClick: () => { try { ReactGA.event({ category: 'ExploreProducts', action: 'secondary_click', label: 'SmartInsights_sign_up' }); } catch { } navigate('/signup'); }
      }
    }
  ];

  const renderImg = (src, fallback, alt) => (
    <img
      className="w-full aspect-[4/5] h-auto object-cover rounded-xl border border-[#026B33]/12 scale-100 transition-transform duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
      src={src}
      alt={alt}
      onError={(e) => { e.currentTarget.src = fallback; }}
    />
  );

  return (
    <>
      <style jsx>{`
        .explore-page {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(1200px 800px at 10% -20%, rgba(2,107,51,0.14) 0%, rgba(2,107,51,0.00) 60%),
            radial-gradient(900px 600px at 110% 120%, rgba(0,161,58,0.12) 0%, rgba(0,161,58,0.00) 60%),
            radial-gradient(700px 500px at 70% 0%, rgba(0,128,96,0.10) 0%, rgba(0,128,96,0.00) 60%),
            linear-gradient(180deg, #ffffff 0%, #f7fff9 100%);
        }

        .orbs {
          position: absolute;
          inset: -10% -10% -10% -10%;
          z-index: 0;
          pointer-events: none;
        }
        .orbs span {
          position: absolute;
          filter: blur(26px);
          opacity: .22;
          mix-blend-mode: screen;
          transform: translateZ(0);
          animation: drift 24s ease-in-out infinite alternate;
        }
        .orbs span:nth-child(1) { width: 420px; height: 420px; left: -6%; top: -6%; background: radial-gradient(circle at 30% 30%, rgba(2,107,51,.36), rgba(2,107,51,0)); transform: translate3d(calc(var(--mx,0)*6px), calc(var(--my,0)*4px), 0); }
        .orbs span:nth-child(2) { width: 360px; height: 360px; right: -5%; top: 10%; background: radial-gradient(circle at 70% 30%, rgba(0,161,58,.30), rgba(0,161,58,0)); animation-duration: 28s; transform: translate3d(calc(var(--mx,0)*-4px), calc(var(--my,0)*2px), 0); }
        .orbs span:nth-child(3) { width: 380px; height: 380px; left: 20%; bottom: -8%; background: radial-gradient(circle at 50% 50%, rgba(0,128,96,.26), rgba(0,128,96,0)); animation-duration: 32s; transform: translate3d(calc(var(--mx,0)*3px), calc(var(--my,0)*-3px), 0); }
        .orbs span:nth-child(4) { width: 300px; height: 300px; right: 18%; bottom: -6%; background: radial-gradient(circle at 40% 60%, rgba(12,120,60,.22), rgba(12,120,60,0)); animation-duration: 36s; transform: translate3d(calc(var(--mx,0)*-2px), calc(var(--my,0)*-2px), 0); }

        @keyframes drift {
          0%   { transform: translate3d(0,0,0); }
          100% { transform: translate3d(20px,-12px,0); }
        }

        .grain {
          position: absolute;
          inset: -20%;
          pointer-events: none;
          z-index: 0;
          opacity: .07;
          background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="140" height="140" viewBox="0 0 140 140"><filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="2" stitchTiles="stitch"/></filter><rect width="100%" height="100%" filter="url(%23n)" opacity="0.6"/></svg>');
          mix-blend-mode: overlay;
          animation: grainShift 6s steps(6) infinite;
        }

        @keyframes grainShift {
          0% { transform: translate(0,0); }
          100% { transform: translate(-10px, 6px); }
        }

        .aurora { position: absolute; inset: 0; z-index: 0; pointer-events:none; }
        .aurora span {
          position: absolute;
          width: 120%; height: 180px;
          left: -10%;
          background: conic-gradient(from 180deg at 50% 50%, rgba(0,255,170,0.0), rgba(0,255,170,0.12), rgba(0,177,64,0.18), rgba(0,161,58,0.0));
          filter: blur(20px) saturate(120%);
          opacity: .35;
          transform: translate3d(calc(var(--mx,0)*8px), calc(var(--my,0)*2px), 0);
          animation: aurora 20s ease-in-out infinite alternate;
        }
        .aurora span:nth-child(1) { top: 8%; }
        .aurora span:nth-child(2) { top: 42%; animation-duration: 26s; }
        .aurora span:nth-child(3) { top: 76%; animation-duration: 32s; }

        @keyframes aurora {
          0% { transform: translate3d(-10px, 0, 0) skewX(-6deg); }
          100% { transform: translate3d(10px, 0, 0) skewX(6deg); }
        }

        .explore-page::before {
          content: '';
          position: absolute;
          inset: -20% -10% -10% -10%;
          pointer-events: none;
          background-image:
            linear-gradient(rgba(2,107,51,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(2,107,51,0.05) 1px, transparent 1px),
            radial-gradient(800px 400px at 20% -10%, rgba(2,107,51,0.08), rgba(2,107,51,0) 70%),
            radial-gradient(700px 400px at 120% 100%, rgba(0,161,58,0.08), rgba(0,161,58,0) 70%);
          background-size: 40px 40px, 40px 40px, auto, auto;
          background-position: 0 0, 0 0, 0 0, 0 0;
          opacity: .42;
          animation: floatGrid 28s ease-in-out infinite alternate;
          transform: translate3d(calc(var(--mx, 0) * 10px), calc(var(--my, 0) * 6px), 0);
        }

        .explore-page::after {
          content: '';
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: radial-gradient(120% 100% at 50% -10%, rgba(0,0,0,0.04), rgba(0,0,0,0) 40%),
                      radial-gradient(120% 120% at 50% 110%, rgba(0,0,0,0.05), rgba(0,0,0,0) 40%);
          mix-blend-mode: multiply;
          transform: translate3d(calc(var(--mx, 0) * -6px), calc(var(--my, 0) * -4px), 0);
        }

        @keyframes floatGrid {
          0%   { transform: translate3d(0, 0, 0) scale(1); }
          100% { transform: translate3d(-20px, -10px, 0) scale(1.02); }
        }

        [data-reveal] {
          opacity: 0;
          transform: translateY(18px) scale(0.98);
          transition: opacity .6s ease, transform .6s cubic-bezier(.2,.8,.2,1);
          transition-delay: var(--delay, 0ms);
        }
        .reveal-in {
          opacity: 1;
          transform: none;
        }

        .product-card::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 18px;
          pointer-events: none;
          padding: 1px;
          -webkit-mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          mask-composite: exclude;
          background: linear-gradient(145deg, rgba(2,107,51,0.0), rgba(2,107,51,0.24), rgba(0,161,58,0.0));
          opacity: 0;
          transition: opacity .35s ease;
        }

        .product-card:hover::after {
          opacity: 1;
        }

        .product-card:hover img {
          transform: scale(1.04);
        }

        .hero-btn {
          position: relative;
          overflow: hidden;
        }
        .hero-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(120% 60% at -20% 0%, rgba(255,255,255,0.35), rgba(255,255,255,0) 60%);
          opacity: 0;
          transition: opacity .35s ease;
        }
        .hero-btn:hover::after {
          opacity: 1;
        }

        .btn-primary {
          background:
            linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0) 40%) top/200% 100% no-repeat,
            linear-gradient(135deg,#026B33, #00A13A);
        }
        .btn-primary:hover {
          background-position: right;
        }

        @media (prefers-reduced-motion: reduce) {
          [data-reveal] { transition: none; transform: none; opacity: 1; }
          .product-card, .product-card img, .btn-primary { transition: none; }
        }

        @media (min-width: 1200px) and (max-height: 900px) {
          .hero-section { padding: clamp(16px, 2.8vw, 28px) 16px 8px; }
          .hero-title { font-size: clamp(20px, 2.6vw, 34px); }
          .hero-subtitle { font-size: clamp(11px, 1vw, 13px); }
          .cards-grid { gap: 10px; padding: 6px clamp(12px, 2.6vw, 24px) 16px; }
          .product-card { min-height: 265px; max-width: 330px; }
          .product-card img { aspect-ratio: 6 / 5; }
        }

        @media (min-width: 1200px) and (max-height: 880px) {
          .hero-section { padding: clamp(20px, 3vw, 36px) 16px 6px; }
          .hero-title { font-size: clamp(22px, 3vw, 34px); }
          .hero-subtitle { font-size: clamp(12px, 1.1vw, 14px); }
          .cards-grid { gap: 10px; padding: 6px clamp(14px, 3vw, 28px) 18px; }
          .product-card { min-height: 270px; max-width: 330px; }
        }

        @media (min-width: 1024px) and (max-height: 780px) {
          .hero-section { padding: 12px 10px 2px; }
          .hero-title { font-size: clamp(18px, 2.2vw, 30px); }
          .hero-subtitle { font-size: clamp(10.5px, 0.95vw, 12.5px); }
          .cards-grid { gap: 6px; padding: 6px 12px 8px; }
          .product-card { min-height: 220px; max-width: 310px; }
          .product-card img { aspect-ratio: 4 / 3; }
        }
      `}</style>

      <div className="explore-page" ref={rootRef}>
        {/* Background ornaments */}
        <div className="orbs" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div className="aurora" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div className="grain" aria-hidden="true" />

        <section className="hero-section pt-32 px-5 pb-6 text-center">
          <h1 className="hero-title font-semibold text-[clamp(26px,3.8vw,48px)] leading-tight text-[#0a3d2a] mx-auto mb-3 max-w-[980px]" data-reveal style={{ '--delay': '80ms' }}>
            EXPLORE OUR PRODUCTS
          </h1>
          <p className="hero-subtitle text-[#5a6d65] text-[clamp(13px,1.4vw,14px)] max-w-[820px] mx-auto" data-reveal style={{ '--delay': '160ms' }}>
            Discover the tools built to elevate training, analysis, and performance. Choose a product below to dive in.
          </p>
          <div className="flex justify-center mt-2.5" data-reveal style={{ '--delay': '240ms' }}>
            <button
              className="hero-btn px-8 py-3 rounded-xl font-semibold text-white bg-gradient-to-br from-[#026B33] to-[#00A13A] border border-transparent shadow-[0_10px_18px_rgba(2,107,51,0.25)] cursor-pointer transition-transform duration-75 active:translate-y-px"
              onClick={() => { try { ReactGA.event({ category: 'ExploreProducts', action: 'cta_click', label: 'hero_sign_up' }); } catch { } navigate('/signup'); }}
            >
              Sign Up
            </button>
          </div>
        </section>

        <section className="cards-grid grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-[clamp(10px,1.6vw,18px)] px-[clamp(16px,3vw,36px)] py-3 pb-[clamp(24px,4vw,44px)] max-w-[1300px] mx-auto md:grid-cols-[repeat(auto-fit,minmax(240px,1fr))] sm:grid-cols-1">
          {cards.map((c, i) => (
            <article key={c.key} className="product-card relative rounded-[18px] bg-white border border-[#026B33]/14 shadow-[0_10px_30px_rgba(2,107,51,0.10)] overflow-hidden flex flex-col min-h-[clamp(260px,28vh,320px)] max-w-[350px] justify-self-center transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_40px_rgba(2,107,51,0.18)]" data-reveal style={{ '--delay': `${120 + i * 120}ms` }}>
              <div className="px-3.5 pt-3.5">
                <div className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full font-semibold text-xs text-[#0a3d2a] bg-[#eef2f1] border border-black/8">
                  <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 3L20 7L12 11L4 7L12 3Z" stroke="#026B33" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M20 12L12 16L4 12" stroke="#026B33" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M20 17L12 21L4 17" stroke="#026B33" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {c.eyebrow}
                </div>
                <h3 className="mt-3.5 mb-1.5 font-semibold text-[#0a3d2a] text-[clamp(18px,2.2vw,24px)]">{c.title}</h3>
                <p className="text-[#5a6d65] text-[13.5px]">{c.text}</p>
              </div>
              <div className="mt-auto bg-gradient-to-b from-[#f1f7f3] to-white p-2 pt-2">
                {renderImg(c.img, c.fallback, c.title)}
              </div>
              <div className="flex gap-2.5 px-3.5 py-2.5 pb-3">
                <button className="btn-primary flex-1 px-3.5 py-3 rounded-lg font-semibold text-white shadow-[0_6px_16px_rgba(2,107,51,0.25)] transition-all active:translate-y-px" onClick={c.primary.onClick}>
                  {c.primary.label}
                </button>
              </div>
            </article>
          ))}
        </section>

        <PdfLightbox
          open={pdfOpen}
          src={pdfSrc}
          title={pdfTitle}
          onClose={() => { try { ReactGA.event({ category: 'ExploreProducts', action: 'pdf_close', label: pdfTitle }); } catch { } setPdfOpen(false); }}
        />

        <PreSignDialog
          open={showPreSign}
          onClose={() => { try { ReactGA.event({ category: 'ExploreProducts', action: 'presign_close', label: 'Match Insights' }); } catch { } setShowPreSign(false); }}
          onSuccess={() => {
            setShowPreSign(false);
            try {
              sessionStorage.setItem('openPreSignOnReturn', '1');
              sessionStorage.setItem('fromPreSignNav', '1');
            } catch { }
            try { ReactGA.event({ category: 'ExploreProducts', action: 'presign_success', label: 'Match Insights' }); } catch { }
            navigate('/signup');
          }}
        />

        <div className="text-center text-[#6b8478] text-xs px-0 py-1.5 pb-6">
          More products are coming soon. Want early access? Head to Sign Up and we'll notify you.
        </div>
      </div>
    </>
  );
}
